import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useContext, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { isDeepEqual } from 'remeda'
import { z } from 'zod'
import { useCurrentUserSetting } from '~/hooks'
import { DurtionHistory } from '~/stores/types'

import { getValueAsArray } from '@hap/analytics'
import {
  Button,
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  Form,
  FormFieldInput,
  Icon,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@hap/components'
import { humanizeSecondsToMinutes } from '@hap/utils'

import { FilterContext } from '../filter-context'
import { renderOperation } from './messages'

const schema = z.object({
  field: z.enum(['handleTime', 'recordingDuration', 'wrapTime', 'talkTime']),
  op: z.string(),
  start: z.coerce.number().min(0).max(999),
  end: z.coerce.number().min(0).max(999),
})

function convertFormToFilter(input: z.infer<typeof schema>): DurtionHistory {
  if (input.op === 'between') {
    return {
      op: input.op,
      values: [`${input.start * 60}`, `${input.end * 60}`],
      field: input.field,
    }
  } else {
    return {
      op: input.op as 'gte' | 'lte',
      value: `${input.start * 60}`,
      field: input.field,
    }
  }
}

export function DurationFilterControl({
  field = 'handleTime',
}: {
  field?: 'handleTime' | 'recordingDuration' | 'wrapTime' | 'talkTime'
}) {
  const defaultHistory = useMemo(() => {
    const DEFAULT_HISTORY: DurtionHistory[] = [
      { op: 'lte', value: `${2 * 60}`, field },
      { op: 'lte', value: `${4 * 60}`, field },
      { op: 'gte', value: `${5 * 60}`, field },
      { op: 'gte', value: `${15 * 60}`, field },
    ]
    return DEFAULT_HISTORY
  }, [field])

  const getHistoryKey = (field: string) => {
    switch (field) {
      case 'handleTime':
        return 'handleTimeHistory'
      case 'recordingDuration':
        return 'recordingDurationHistory'
      case 'wrapTime':
        return 'wrapTimeHistory'
      case 'talkTime':
        return 'talkTimeHistory'
      default:
        return 'handleTimeHistory'
    }
  }

  const [history, setHistory] = useCurrentUserSetting(
    'lookup',
    getHistoryKey(field),
    defaultHistory,
  )
  const { onSelect, filter } = useContext(FilterContext)
  const values = getValueAsArray(filter)
  const form = useForm({
    resolver: zodResolver(schema),
    values: {
      field: field,
      op: filter ? (filter.op as 'gte') : 'gte',
      start: values[0] ? parseInt(values[0] as string) / 60 : 0,
      end: values[1] ? parseInt(values[1] as string) / 60 : 0,
    },
  })

  const onSubmit = form.handleSubmit((data) => {
    if (data.op === 'between') {
      if (data.end <= data.start) {
        return form.setError('end', { message: 'End must be greater than start' })
      }
    }
    const parsedFilter = convertFormToFilter(data)
    const nextDurationHistory: DurtionHistory[] = [parsedFilter, ...history]
    setHistory(
      nextDurationHistory.reduce<DurtionHistory[]>((acc, filter, index) => {
        if (index > 5) return acc
        if (acc.some((f) => isDeepEqual(filter, f))) return acc
        return [...acc, filter]
      }, []),
    )
    onSelect(parsedFilter)
  })

  const handleRemove = useCallback(
    (index: number) => {
      setHistory(history.filter((_, i) => i !== index))
    },
    [history],
  )

  const isBetween = form.watch('op') === 'between'

  return (
    <div>
      <Form {...form}>
        <form onSubmit={onSubmit}>
          <div className="grid gap-1 border-b p-2">
            <div className="flex items-center gap-1">
              <ToggleGroup
                value={form.watch('op')}
                type="single"
                onValueChange={(value) => {
                  form.setValue('op', value)
                }}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem value="gte" className="text-lg font-bold">
                      <Icon name="greater-than-or-equal" />
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent>Greater than</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem value="lte" className="text-lg font-bold">
                      <Icon name="less-than-or-equal" />
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent>Less than</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem value="between" className="text-xl font-bold">
                      <Icon name="arrows-horizontal" />
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent>Between</TooltipContent>
                </Tooltip>
              </ToggleGroup>
              <FormFieldInput
                type="text"
                name="start"
                control={form.control}
                suffix={isBetween ? 'Start' : 'Mins'}
                maxLength={3}
                inputClassName="h-auto"
                autoFocus
              />
              {isBetween && (
                <FormFieldInput
                  type="text"
                  name="end"
                  control={form.control}
                  maxLength={3}
                  suffix={isBetween ? 'End' : 'Mins'}
                  inputClassName="h-auto"
                />
              )}
              <Button type="submit" icon="check" size="icon-sm" className="shrink-0" />
            </div>
          </div>
        </form>
      </Form>

      {history.length > 0 && (
        <Command>
          <CommandList>
            <CommandGroup heading="History">
              {history.map((filter, key) => {
                const operatorLabel = renderOperation(filter.op)
                const valueLabel =
                  'values' in filter
                    ? filter.values
                        .map((v) => humanizeSecondsToMinutes(v as string))
                        .join(' and ')
                    : humanizeSecondsToMinutes(filter.value as string)
                return (
                  <CommandItem
                    key={key}
                    onSelect={() => onSelect(filter)}
                    value={`${filter.op} ${valueLabel}`}
                  >
                    <span className="flex grow items-center gap-2">
                      {operatorLabel}
                      {valueLabel}
                    </span>
                    <Button
                      icon="x"
                      size="icon-sm"
                      variant="ghost"
                      className="shrink-0 opacity-0 group-aria-selected:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemove(key)
                      }}
                    />
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      )}
    </div>
  )
}
